<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useSettingsStore } from '@/stores/settings';

const auth = useAuthStore();
const ui = useUiStore();
const settings = useSettingsStore();
// patchnummer = commit-telling; -dev = lokale build. Tooltip toont de exacte commit.
const versionLabel = `v${__APP_VERSION__}`;
const versionTitle = __APP_COMMIT__ ? `Commit ${__APP_COMMIT__}` : 'Lokale dev-build';
const roleLabel = computed(
  () =>
    ({ admin: 'Beheerder', approver: 'Goedkeurder', user: 'Gebruiker', pending: 'Wacht op goedkeuring' })[
      auth.user?.role ?? 'user'
    ],
);
const envBadge = computed(() => settings.unit4Env);
</script>

<template>
  <nav class="navbar navbar-expand kl-navbar fixed-top shadow-sm px-3">
    <button
      v-if="auth.isActive"
      class="btn btn-link text-primary me-2 d-lg-none p-1"
      aria-label="Menu"
      @click="ui.sidebarMobileOpen = !ui.sidebarMobileOpen"
    >
      <i class="bi bi-list fs-3"></i>
    </button>
    <router-link class="navbar-brand d-flex align-items-center gap-2" to="/products">
      <img src="/img/klinger_logo.svg" alt="Klinger" />
      <span class="fw-semibold text-primary d-none d-sm-inline">Klingerator</span>
    </router-link>

    <div class="ms-3 d-none d-md-block">
      <span
        v-if="envBadge"
        class="badge badge-env"
        :class="envBadge === 'prod' ? 'bg-danger' : 'bg-primary'"
        :title="envBadge === 'prod' ? 'Verbonden met Unit4 PRODUCTIE' : 'Verbonden met Unit4 TEST'"
      >
        UNIT4 {{ envBadge === 'prod' ? 'PRODUCTIE' : 'TEST' }}
      </span>
    </div>

    <div class="ms-auto d-flex align-items-center gap-3">
      <!-- Zijbalk in-/uitklappen (desktop) — rechts naast het accountmenu -->
      <button
        v-if="auth.isActive"
        class="btn btn-link text-primary d-none d-lg-inline p-1"
        :aria-label="ui.sidebarCollapsed ? 'Menu uitklappen' : 'Menu inklappen'"
        :title="ui.sidebarCollapsed ? 'Menu uitklappen' : 'Menu inklappen'"
        @click="ui.toggleSidebar()"
      >
        <i class="bi" :class="ui.sidebarCollapsed ? 'bi-layout-sidebar' : 'bi-layout-sidebar-inset'"></i>
      </button>
      <!-- Assistent (rechterkolom) tonen/verbergen — spiegelbeeld van de zijbalk-knop -->
      <button
        v-if="auth.isActive"
        class="btn btn-link text-primary p-1"
        :class="{ 'kl-chat-toggle-active': ui.chatOpen }"
        :aria-label="ui.chatOpen ? 'Assistent verbergen' : 'Assistent tonen'"
        :aria-pressed="ui.chatOpen"
        :title="ui.chatOpen ? 'Assistent verbergen' : 'Assistent tonen'"
        data-testid="chat-toggle"
        @click="ui.toggleChat()"
      >
        <i
          class="bi"
          :class="ui.chatOpen ? 'bi-layout-sidebar-inset-reverse' : 'bi-layout-sidebar-reverse'"
        ></i>
      </button>
      <div v-if="auth.user" class="dropdown">
        <button
          class="btn btn-outline-primary btn-sm dropdown-toggle d-flex align-items-center gap-2"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i class="bi bi-person-circle"></i>
          <span class="d-none d-sm-inline">{{ auth.user.name || auth.user.email }}</span>
        </button>
        <ul class="dropdown-menu dropdown-menu-end shadow">
          <li class="dropdown-header">
            <div class="fw-semibold">{{ auth.user.name }}</div>
            <div class="small text-muted">{{ auth.user.email }}</div>
            <span class="badge bg-light text-dark mt-1">{{ roleLabel }}</span>
          </li>
          <li><hr class="dropdown-divider" /></li>
          <template v-if="auth.isAdmin">
            <li>
              <router-link class="dropdown-item" to="/admin/users"
                ><i class="bi bi-people me-2"></i>Gebruikers</router-link
              >
            </li>
            <li>
              <router-link class="dropdown-item" to="/admin/settings"
                ><i class="bi bi-gear me-2"></i>Instellingen</router-link
              >
            </li>
            <li>
              <router-link class="dropdown-item" to="/admin/sync-log"
                ><i class="bi bi-clock-history me-2"></i>Sync-historie</router-link
              >
            </li>
            <li><hr class="dropdown-divider" /></li>
          </template>
          <li>
            <button class="dropdown-item" @click="auth.logout()">
              <i class="bi bi-box-arrow-right me-2"></i>Uitloggen
            </button>
          </li>
        </ul>
      </div>
      <span class="kl-version" :title="versionTitle">{{ versionLabel }}</span>
    </div>
  </nav>
</template>
